import { Entity, GPSTrack, HexColor, Location } from '@nx-smartmonkey/shared/domain';

import { ROUTE_END_LOCATION, ROUTE_START_LOCATION } from '../../constants/Routes';
import { STOP_STATUS } from '../../constants/Stops';
import { kmToSM } from '../../helpers/Units';
import { DistanceType } from '../driversProject/DriversProject';
import { Stop } from '../stop/Stop';
import { RouteStatus } from './RouteStatus';
import { RouteSurvey } from './RouteSurvey';

export interface UpdateRouteProps {
  label?: string;
  execution_date?: Date;
  start_location?: Location;
  end_location?: Location;
}

interface RouteProps {
  project_id: string;
  external_id?: string;
  vehicle_id?: string;
  label?: string;
  color?: HexColor;
  phone?: string;
  start_location?: Location;
  end_location?: Location;
  start_date?: Date;
  end_date?: Date;
  estimated_end_time?: Date;
  status: RouteStatus;
  planned_track?: string;
  planned_start_time?: number;
  planned_end_time?: number;
  last_position?: GPSTrack;
  total_distance: number;

  tracing_percent?: number;
  distance_traveled?: number;

  stops: Stop[];
  plan_id: string;
  plan_label?: string;
  plan_execution_date: Date;
  survey?: RouteSurvey;

  created_at: Date;
  updated_at?: Date;
  created_by?: string;
}

export class Route extends Entity<RouteProps> {
  get id(): string {
    return this._id!;
  }
  get plan_id(): string {
    return this.props.plan_id;
  }
  get project_id(): string {
    return this.props.project_id;
  }
  get external_id(): string | undefined {
    return this.props.external_id;
  }
  get vehicle_id(): string | undefined {
    return this.props.vehicle_id;
  }
  get label(): string | undefined {
    return this.props.label;
  }
  get phone(): string | undefined {
    return this.props.phone;
  }
  get start_location(): Location | undefined {
    return this.props.start_location;
  }
  get end_location(): Location | undefined {
    return this.props.end_location;
  }
  get start_date(): Date | undefined {
    return this.props.start_date;
  }
  set start_date(start_date: Date | undefined) {
    this.props.start_date = start_date;
  }
  get end_date(): Date | undefined {
    return this.props.end_date;
  }
  set end_date(end_date: Date | undefined) {
    this.props.end_date = end_date;
  }
  get estimated_end_time(): Date | undefined {
    return this.props.estimated_end_time;
  }
  get status(): RouteStatus {
    return this.props.status;
  }
  set status(status: RouteStatus) {
    this.props.status = status;
  }
  get planned_track(): string | undefined {
    return this.props.planned_track;
  }
  get planned_start_time(): number | undefined {
    return this.props.planned_start_time;
  }
  get planned_end_time(): number | undefined {
    return this.props.planned_end_time;
  }
  get last_position(): GPSTrack | undefined {
    return this.props.last_position;
  }
  get tracing_percent(): number | undefined {
    return this.props.tracing_percent;
  }
  get distance_traveled(): number | undefined {
    return this.props.distance_traveled;
  }
  get stops(): Stop[] {
    return this.props.stops.filter((s) => !s.isSpecificStop);
  }
  set stops(stops: Stop[]) {
    this.props.stops = stops;
  }
  get plan_label(): string | undefined {
    return this.props.plan_label;
  }
  get plan_execution_date(): Date {
    return this.props.plan_execution_date;
  }
  get created_by(): string | undefined {
    return this.props.created_by;
  }
  get survey(): RouteSurvey | undefined {
    return this.props.survey;
  }
  set survey(survey: RouteSurvey | undefined) {
    this.props.survey = survey;
  }
  get created_at(): Date {
    return this.props.created_at;
  }
  get updated_at(): Date | undefined {
    return this.props.updated_at;
  }
  get color(): HexColor | undefined {
    return this.props.color;
  }
  get total_distance(): number {
    return this.props.total_distance;
  }

  private constructor(props: RouteProps, id: string) {
    super(props, id);
  }

  static create(props: RouteProps, id: string) {
    return new Route(props, id);
  }

  /**
   * Total distance is the amount of distance_to_next_stop of each
   * stop and the first route service distance_to_previous_stop.
   */
  getTotalDistance(distanceUnits: DistanceType): string {
    const finalTotalDistance = this.props.total_distance ? kmToSM(this.props.total_distance, distanceUnits) : 0;
    return finalTotalDistance > 100 ? Math.floor(finalTotalDistance).toFixed(0) : finalTotalDistance.toFixed(1);
  }

  getNumberPendingStops(): number {
    return this.stops.filter((s) => !s.isSpecificStop && s.status === STOP_STATUS.pending).length;
  }

  getNonSpecificStops(): Stop[] {
    return this.stops.filter((s) => !s.isSpecificStop);
  }

  getAllStops(): Stop[] {
    return this.props.stops;
  }

  // Methods to get the stop by id
  getStopById(stopId: string): Stop | undefined {
    return this.props.stops.find((s) => s.id === stopId);
  }

  // This was used when stop details had only one stop behind and one ahead ---------- (FROM HERE)
  // getSortedPrevStopById(stopId: string): Stop | undefined {
  //   const sortedStops = this.getSortedStopList({});
  //   const index = sortedStops.findIndex((s) => s.id === stopId);
  //   return index === -1 ? undefined : sortedStops[index - 1];
  // }

  // getPrevStopById(stopId: string): Stop | undefined {
  //   const index = this.props.stops.findIndex((s) => s.id === stopId);
  //   return index === -1 ? undefined : this.props.stops[index - 1];
  // }

  // getSortedPostStopById(stopId: string): Stop | undefined {
  //   const sortedStops = this.getSortedStopList({});
  //   const index = sortedStops.findIndex((s) => s.id === stopId);
  //   return index === -1 ? undefined : sortedStops[index + 1];
  // }

  // getPostStopById(stopId: string): Stop | undefined {
  //   const index = this.props.stops.findIndex((s) => s.id === stopId);
  //   return index === -1 ? undefined : this.props.stops[index + 1];
  // }
  // This was used when stop details had only one stop behind and one ahead ---------- (TO HERE)

  // Methods to get the specifics stop in case the route have them
  getRouteStartStop(): Stop | undefined {
    const index = this.props.stops.findIndex((s) => s.id === ROUTE_START_LOCATION);
    return index === -1 ? undefined : this.props.stops[index];
  }

  getRouteEndStop(): Stop | undefined {
    const index = this.props.stops.findIndex((s) => s.id === ROUTE_END_LOCATION);
    return index === -1 ? undefined : this.props.stops[index];
  }

  getDeliveryByPickupId = (pickupId: string): Stop | undefined => {
    return this.stops.find(
      (stop: Stop) => stop.status === STOP_STATUS.pending && stop.type === `delivery` && stop.pickup_id === pickupId
    );
  };

  getSortedStopList = ({
    withStartStop = true,
    withEndStop = true,
  }: {
    withStartStop?: Boolean;
    withEndStop?: Boolean;
  }): Stop[] => {
    const sortedStops = [] as Stop[];

    if (withStartStop) {
      const startStop = this.getRouteStartStop();
      if (startStop) {
        sortedStops.push(startStop);
      }
    }

    sortedStops.push(
      ...this.stops
        .filter((stop) => !stop.isSpecificStop)
        .sort((a, b) => {
          const aCreatedAt = a.report?.created_at ?? 0;
          const bCreatedAt = b.report?.created_at ?? 0;
          if (aCreatedAt === 0 && bCreatedAt === 0) {
            return 0;
          } else if (aCreatedAt === 0) {
            return 1;
          } else if (bCreatedAt === 0) {
            return -1;
          } else {
            return new Date(aCreatedAt).getTime() - new Date(bCreatedAt).getTime();
          }
        })
    );

    if (withEndStop) {
      const endStop = this.getRouteEndStop();
      if (endStop) {
        sortedStops.push(endStop);
      }
    }

    return sortedStops;
  };

  deleteStop = (stopId: string): void => {
    const stop = this.props.stops.find((stop) => stop.id === stopId);
    if (!stop) return;

    const stopOrder = stop.order;
    this.props.stops = this.props.stops.filter((s) => s.id !== stopId);
    this.getSortedStopList({ withEndStop: false, withStartStop: false }).map((s) => {
      if (s.order > stopOrder) s.order = s.order - 1;
      return s;
    });
  };

  update = (props: UpdateRouteProps): void => {
    if (props.label) this.props.label = props.label;
    if (props.execution_date) this.props.plan_execution_date = props.execution_date;
    // Also update start and end Stop locations
    if (props.start_location) {
      this.props.start_location = props.start_location;
    }
    if (props.end_location) {
      this.props.end_location = props.end_location;
    }
  };
}
